import { useEffect, useState } from 'react';
import { animate, useMotionValue, useReducedMotion } from 'framer-motion';

interface AnimatedCountProps {
  value: number;
  /** ms — slightly longer rolls feel more satisfying on bigger numbers */
  duration?: number;
}

/**
 * Rolls from the previous displayed value to the next one, easing out.
 * Respects prefers-reduced-motion (snaps instantly when set).
 */
export function AnimatedCount({ value, duration }: AnimatedCountProps) {
  const reduce = useReducedMotion();
  const mv = useMotionValue(value);
  const [display, setDisplay] = useState(value);

  useEffect(() => {
    if (reduce) {
      setDisplay(value);
      return;
    }
    // Scale duration with magnitude — 0..20 ≈ 600ms, 20..100 ≈ 900ms, larger ≈ 1100ms.
    const d = duration ?? (value > 100 ? 1.1 : value > 20 ? 0.9 : 0.6);
    const controls = animate(mv, value, {
      duration: d,
      ease: [0.22, 0.61, 0.36, 1],
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return () => controls.stop();
  }, [value, duration, reduce, mv]);

  return <>{display.toLocaleString()}</>;
}
